import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import toast from '@liquidweb-dev/undercurrent/toast';

import FadeCollapse from 'components/standard/FadeCollapse';
import { AcceptDispatchOkI } from 'modules/creditCard';
import useBillingPaymentProfileDetails from 'modules/queries/billing/payment/profile/useDetails';
import useBillingBalance from 'modules/queries/billing/useBalance';
import {
	ConfirmDefaultPaymentContent,
	ConfirmOneTimePaymentContent,
	NewCardContent,
	StartContent,
} from './contents';
import dialogMakePaymentContext from './dialogMakePaymentContext';
import titles, { type DialogMakePaymentStep } from './titles';

export interface DialogMakePaymentProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
}

const initialFormValues = {
	amount: '0',
	payFullBalance: false,
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
};

const initialFormErrors = {
	amount: '',
	payFullBalance: '',
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
};

const DialogMakePayment = ({
	onClose,
	open,
	...restDialogProps
}: DialogMakePaymentProps) => {
	const [step, setStep] = useState<DialogMakePaymentStep>('start');
	const [opaqueData, setOpaqueData] = useState<
		AcceptDispatchOkI['opaqueData'] | null
	>(null);
	const [formValues, setFormValues] = useState(initialFormValues);
	const [formErrors, setFormErrors] = useState(initialFormErrors);

	const { data: paymentProfileDetails } = useBillingPaymentProfileDetails({});
	const { payment_method: paymentMethod } = paymentProfileDetails || {};

	const { data: billingBalanceData } = useBillingBalance({
		enabled: Boolean(open),
	});
	const { accountBalanceDisplay } = billingBalanceData || {};

	const clearErrors = () => setFormErrors(initialFormErrors);

	const resetStep = useCallback(() => {
		if (!paymentMethod) return;

		if (paymentMethod === 'check') {
			setStep('choosePaymentMethod');
		}

		setStep('start');
	}, [paymentMethod]);

	/** should be true if there is an account balance */
	const shouldInitAsPayingFullBalance = useMemo(() => {
		return Boolean(accountBalanceDisplay && Number(accountBalanceDisplay) > 0);
	}, [accountBalanceDisplay]);

	const resetForm = useCallback(() => {
		setFormValues({
			...initialFormValues,
			amount: accountBalanceDisplay || '0',
			payFullBalance: shouldInitAsPayingFullBalance,
		});
		clearErrors();
	}, [accountBalanceDisplay, shouldInitAsPayingFullBalance]);

	// reset step and form when dialog is opened
	useEffect(() => {
		if (open) {
			resetStep();

			resetForm();
		}
	}, [open, resetStep, resetForm]);

	const clearSensitiveFormValues = () => {
		setFormValues((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
	};

	const handleStepChange = (newStep: DialogMakePaymentStep) => {
		setStep(newStep);
		clearErrors();
	};

	const handleSubmitSuccess = () => {
		onClose();
		toast.success('Payment successful', {
			description: `Your payment of ${formatCurrency(formValues.amount)} was confirmed`,
		});
	};

	const handleSubmitError = () => {
		toast.danger('Payment failed', {
			description: 'We were unable to process your payment using that method',
		});
		resetStep();
	};

	return (
		<Dialog
			maxWidth="sm"
			fullWidth
			onClose={onClose}
			open={open}
			{...restDialogProps}
		>
			<DialogTitle onClose={onClose}>{titles[step]}</DialogTitle>
			<DialogContent>
				<dialogMakePaymentContext.Provider
					value={{
						formErrors,
						formValues,
						opaqueData,
						step,
						clearSensitiveFormValues,
						onSubmitSuccess: handleSubmitSuccess,
						onSubmitError: handleSubmitError,
						setFormErrors,
						setFormValues,
						setOpaqueData,
						setStep: handleStepChange,
					}}
				>
					<FadeCollapse in={step === 'start' || step === 'choosePaymentMethod'}>
						<StartContent />
					</FadeCollapse>

					<FadeCollapse in={step === 'useNewCard'}>
						<NewCardContent />
					</FadeCollapse>

					<FadeCollapse in={step === 'confirmPayment'}>
						<ConfirmDefaultPaymentContent />
					</FadeCollapse>

					<FadeCollapse in={step === 'confirmOneTimePayment'}>
						<ConfirmOneTimePaymentContent />
					</FadeCollapse>
				</dialogMakePaymentContext.Provider>
			</DialogContent>
		</Dialog>
	);
};

export default DialogMakePayment;
